import { type PageContentEditorBlock } from '@app/components/blocks/pageContentEditor/PageContentEditor'
import type { Initiative } from '@schema/initiative'
import type { PageContentBlock } from '@schema/pageContentBlock'

type PositionedBlock = PageContentBlock & {
  $position?: string
  $positionIndex?: number
}

const sortOverviewTextBlocks = (blocks: PositionedBlock[]): PositionedBlock[] =>
  [...blocks].sort((a, b) => {
    const oa = a.order ?? 0
    const ob = b.order ?? 0
    if (oa !== ob) {
      return oa - ob
    }
    return (a.$positionIndex ?? 0) - (b.$positionIndex ?? 0)
  })

const isOverviewTextBlock = (b: PositionedBlock): boolean =>
  b.type === 'text' && b.$position === 'overviewTab'

export const mapOverviewTextBlocks = (
  contentBlocks: Initiative['contentBlocks'] | undefined,
  placeholder?: { title?: string; body?: string }[],
): PageContentEditorBlock[] => {
  const filtered = (contentBlocks ?? []).filter(isOverviewTextBlock)
  const mapped = sortOverviewTextBlocks(filtered).map((b) => ({
    id: b.id,
    type: 'text' as const,
    title: b.title ?? '',
    body: b.body ?? '<p></p>',
    clientKey: b.id?.toString() ?? crypto.randomUUID(),
  }))
  const formatted = ensureMinOneTextBlock(mapped)
  const enriched = formatted.map((b, i) => ({
    ...b,
    titlePlaceholder: placeholder?.[i]?.title,
    bodyPlaceholder: placeholder?.[i]?.body,
  }))
  return enriched
}

export const emptyTextBlock = (): PageContentEditorBlock => ({
  type: 'text',
  title: '',
  body: '',
  clientKey: crypto.randomUUID(),
})

export const contentBlocksFingerprint = (
  blocks: PageContentEditorBlock[],
): string =>
  JSON.stringify(
    blocks.map((b) => ({
      id: b.id,
      type: b.type,
      title: b.title,
      body: b.body,
    })),
  )

const ensureMinOneTextBlock = (
  blocks: PageContentEditorBlock[],
): PageContentEditorBlock[] => (blocks.length > 0 ? blocks : [emptyTextBlock()])

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim()

export const isOverviewPageContentStepValid = (
  blocks: PageContentEditorBlock[],
): boolean => {
  return blocks.every(
    (b) => b.title.trim().length > 0 && stripHtml(b.body).length > 0,
  )
}

export const persistInitiativePageContentBlocks = async (
  call: (name: string, payload: unknown) => Promise<number>,
  initiativeId: number,
  serverSnapshot: PageContentEditorBlock[],
  local: PageContentEditorBlock[],
): Promise<void> => {
  const serv = serverSnapshot

  const serverById = new Map(serv.filter((b) => b.id).map((b) => [b.id, b]))

  const removed = serv.filter((s) => s.id && !local.some((l) => l.id === s.id))
  const removePromises = removed.map((r) => {
    return call('delete-page-content-block', { id: r.id })
  })

  const updatePromises = local.map((block, index) => {
    if (block.id) {
      const prev = serverById.get(block.id)
      const prevIndex = prev ? serv.indexOf(prev) : -1

      if (
        !prev ||
        prev.title !== block.title ||
        prev.body !== block.body ||
        prevIndex !== index
      ) {
        return call('edit-page-content-block', {
          id: block.id,
          title: block.title,
          body: block.body,
          order: index,
        })
      }
    } else {
      return call('create-page-content-block', {
        parent: 'initiative',
        initiativeId,
        position: { tab: 'overviewTab', index },
        title: block.title,
        body: block.body,
      })
    }

    return Promise.resolve()
  })

  await Promise.all(updatePromises.concat(removePromises))
}
